import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import { SkeletonItem } from 'components/reusable/SkeletonItem'
import EmployerInvoicesPaths from 'rails_routes/Employers/EmployerInvoicesPaths'
import { Table } from 'slab'
import { convertGrapeMoneyToNumber, formatMoney } from 'utils'
import {
  type CombinedInvoice,
  formatInvoiceDate,
  formatInvoiceTitle,
  getDefaultYear,
  getInvoiceDate,
  INVOICE_TYPE,
} from 'utils/billing'
import {
  useGetEmployersEmployerInvoices,
  useGetEmployersEmployerInvoicesFirstBillingCycleStatus,
  useGetEmployersEmployerInvoicesYears,
  useGetEmployersStripeInvoices,
  useGetEmployersTodos,
} from 'utils/web_api'
import {
  type EmployersEmployerInvoice,
  type EmployersEmployerInvoiceYears,
  type EmployersStripeInvoice,
  type EmployersTodosList,
} from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { BillingYearDropdown } from './BillingYearDropdown'
import { InvoiceStatusBadge } from './InvoiceStatusBadge'
import { FirstBillModal } from './modals/FirstBillModal'
import TodoList from '../Todos/TodoList'

const SKELETON_ROWS = Array.from({ length: 5 }, (_, index) => (
  <Table.Row key={`skeleton-${index}`}>
    <Table.Cell>
      <SkeletonItem widthClass='w-full' />
    </Table.Cell>
    <Table.Cell>
      <SkeletonItem widthClass='w-1/2' />
    </Table.Cell>
    <Table.Cell>
      <SkeletonItem widthClass='w-20' />
    </Table.Cell>
    <Table.Cell>
      <SkeletonItem widthClass='w-24' />
    </Table.Cell>
    <Table.Cell>
      <SkeletonItem widthClass='w-16' />
    </Table.Cell>
  </Table.Row>
))

interface BillingPageProps {
  goToEmployerInvoice: (invoiceId: string) => void
  goToPlatformFeeInvoice: (invoiceId: string) => void
  todosData?: EmployersTodosList
  yearsData?: EmployersEmployerInvoiceYears
  isYearsLoading?: boolean
}

export function BillingPage ({
  goToEmployerInvoice,
  goToPlatformFeeInvoice,
  todosData,
  yearsData,
  isYearsLoading,
}: BillingPageProps) {
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [isFirstBillModalOpen, setIsFirstBillModalOpen] = useState(false)

  const [isFirstBillModalDismissed, setIsFirstBillModalDismissed] = useLocalStorage(
    'thatch-billing-first-bill-modal-dismissed-v1',
    false
  )

  const { data: firstBillingCycleStatus } = useGetEmployersEmployerInvoicesFirstBillingCycleStatus()

  const { data, isLoading, isError } = useGetEmployersEmployerInvoices({
    year: selectedYear,
  })

  const {
    data: stripeInvoicesData,
    isLoading: isStripeInvoicesLoading,
    isError: isStripeInvoicesError,
  } = useGetEmployersStripeInvoices({
    year: selectedYear,
  })

  const allInvoices: EmployersEmployerInvoice[] = data?.invoices || []
  const currentInvoice = data?.current_invoice
  const stripeInvoices: EmployersStripeInvoice[] = stripeInvoicesData?.invoices || []
  const allYears: number[] = yearsData?.years || [currentYear]

  const isInFirstBillingCycle = firstBillingCycleStatus?.in_first_billing_cycle === true

  const hasTodos = todosData?.todos?.some(todo => todo.config_key === 'ach_authorization')

  const combinedInvoices = useMemo<CombinedInvoice[]>(() => {
    const employerInvoicesWithType: CombinedInvoice[] = allInvoices.map((invoice) => ({
      ...invoice,
      type: INVOICE_TYPE.EMPLOYER,
    }))
    const stripeInvoicesWithType: CombinedInvoice[] = stripeInvoices.map((invoice) => ({
      ...invoice,
      type: INVOICE_TYPE.STRIPE,
    }))
    const combined = [
      ...(currentInvoice ? [currentInvoice] : []),
      ...employerInvoicesWithType,
      ...stripeInvoicesWithType,
    ]
    return combined.sort((a, b) => {
      if (a.id === 'current') return -1
      if (b.id === 'current') return 1
      const dateA = getInvoiceDate(a)
      const dateB = getInvoiceDate(b)
      return dateB.getTime() - dateA.getTime()
    })
  }, [allInvoices, currentInvoice, stripeInvoices])

  useEffect(() => {
    if (!isYearsLoading && allYears.length > 0) {
      setSelectedYear((prevYear) => {
        if (!allYears.includes(prevYear)) {
          return getDefaultYear(allYears, currentYear, prevYear)
        }
        return prevYear
      })
    }
  }, [allYears, currentYear, isYearsLoading])

  useEffect(() => {
    if (isInFirstBillingCycle && !isFirstBillModalDismissed) {
      setIsFirstBillModalOpen(true)
    }
  }, [isFirstBillModalDismissed, isInFirstBillingCycle])

  const handleYearChange = (value: string) => {
    setSelectedYear(Number(value))
  }

  const handleInvoiceClick = (invoice: CombinedInvoice) => {
    if (invoice.id === 'current') {
      return
    }
    const isEmployerInvoice = invoice.type === INVOICE_TYPE.EMPLOYER

    if (isEmployerInvoice) {
      goToEmployerInvoice(invoice.id)
    } else {
      goToPlatformFeeInvoice(invoice.id)
    }
  }

  if (isError || isStripeInvoicesError) {
    return (
      <div>
        <div className='mt-2 mb-4 p-4 text-sm text-slate-600 border-2 border-red-500 rounded-lg shadow-md'>
          There was an error fetching invoices. Please reach out to{' '}
          <a href='mailto:support@thatch.com'>support@thatch.com</a> if you
          continue to experience this error.
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className='mb-6'>
        <h1 className='heading-lg'>Billing</h1>
      </div>

      {!!hasTodos && (
        <div className='mb-6'>
          <TodoList category='onboarding' filter={(todo) => todo.config_key === 'ach_authorization'} />
        </div>
      )}

      <div className='mb-4 flex items-center gap-4 mt-10'>
        <h3 className='heading-sm'>All bills</h3>
        <BillingYearDropdown
          yearsAvailable={allYears}
          selectedYear={selectedYear}
          handleSetYear={handleYearChange}
          isLoading={isYearsLoading}
        />
      </div>

      <div className='mb-6 rounded-2xl shadow-[0_1px_4px_rgba(0,0,0,0.1)] border border-gray-200 overflow-hidden bg-white py-4'>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.ColumnHeader>Bill</Table.ColumnHeader>
              <Table.ColumnHeader>Date billed</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Invoice #</Table.ColumnHeader>
              <Table.ColumnHeader>Cost</Table.ColumnHeader>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {isLoading || isStripeInvoicesLoading
              ? (
                  SKELETON_ROWS
                )
              : combinedInvoices.length === 0
                ? (
                  <Table.Row>
                    <Table.Cell colSpan={5}>
                      <div className='text-center text-gray-500 py-8'>
                        No invoices found for {selectedYear}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                  )
                : (
                    combinedInvoices.map((invoice: CombinedInvoice) => {
                      const invoiceDate = getInvoiceDate(invoice)
                      const isCurrent = invoice.id === 'current'

                      return (
                        <Table.Row
                          key={invoice.id}
                          onClick={() => handleInvoiceClick(invoice)}
                          accessibleLabel={`View details for ${formatInvoiceTitle(invoice)}`}
                          style={isCurrent ? { cursor: 'not-allowed' } : undefined}
                        >
                          <Table.RowHeader>{formatInvoiceTitle(invoice)}</Table.RowHeader>
                          <Table.Cell>
                            {formatInvoiceDate(invoiceDate)}
                          </Table.Cell>
                          <Table.Cell>
                            <InvoiceStatusBadge invoice={invoice} />
                          </Table.Cell>
                          <Table.Cell>
                            {invoice.invoice_number || (invoice.id === 'current' ? '—' : invoice.id)}
                          </Table.Cell>
                          <Table.Cell>
                            {formatMoney(convertGrapeMoneyToNumber(invoice.total))}
                          </Table.Cell>
                        </Table.Row>
                      )
                    })
                  )}
          </Table.Body>
          <tfoot>
            <tr>
              <td colSpan={5} className='border-0 p-6'>
                <button
                  type='button'
                  onClick={() => {
                    window.segment?.track('[ER] How does billing work at Thatch clicked', {})
                    setIsFirstBillModalOpen(true)
                  }}
                  className='text-thatch-purple-secondary pb-0 inline-block underline decoration-dotted hover:decoration-solid underline-offset-[0.25rem] cursor-help'
                >
                  <span>How does billing work at Thatch</span>
                </button>
              </td>
            </tr>
          </tfoot>
        </Table>
      </div>

      <FirstBillModal
        open={isFirstBillModalOpen}
        onOpenChange={(open) => {
          setIsFirstBillModalOpen(open)
          if (!open) setIsFirstBillModalDismissed(true)
        }}
      />
    </div>
  )
}

export function RailsBillingPage () {
  const { data: todosData } = useGetEmployersTodos({
    'categories[]': ['onboarding'],
  })

  const {
    data: yearsData,
    isLoading: isYearsLoading
  } = useGetEmployersEmployerInvoicesYears()

  const location = useWindowLocation()

  const goToEmployerInvoice = useCallback((invoiceId: string) => {
    location.assign(EmployerInvoicesPaths.show({ id: invoiceId }))
  }, [])

  const goToPlatformFeeInvoice = useCallback((invoiceId: string) => {
    location.assign(EmployerInvoicesPaths.showPlatformFee({ id: invoiceId }))
  }, [])

  return (
    <BillingPage
      goToEmployerInvoice={goToEmployerInvoice}
      goToPlatformFeeInvoice={goToPlatformFeeInvoice}
      todosData={todosData}
      yearsData={yearsData}
      isYearsLoading={isYearsLoading}
    />
  )
}
